/**
 * Compact Tiptap rich-text editor used inside each lesson block.
 * Provides a toolbar with common formatting options and emits HTML on save.
 */
import React, { useRef } from 'react';
import { useEditor, EditorContent, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material';
import FormatBoldOutlinedIcon from '@mui/icons-material/FormatBoldOutlined';
import FormatItalicOutlinedIcon from '@mui/icons-material/FormatItalicOutlined';
import FormatUnderlinedOutlinedIcon from '@mui/icons-material/FormatUnderlinedOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import LooksOneOutlinedIcon from '@mui/icons-material/LooksOneOutlined';
import LooksTwoOutlinedIcon from '@mui/icons-material/LooksTwoOutlined';
import Looks3OutlinedIcon from '@mui/icons-material/Looks3Outlined';
import FormatAlignLeftOutlinedIcon from '@mui/icons-material/FormatAlignLeftOutlined';
import FormatAlignCenterOutlinedIcon from '@mui/icons-material/FormatAlignCenterOutlined';
import FormatAlignRightOutlinedIcon from '@mui/icons-material/FormatAlignRightOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';

import { authoredContentSx } from '@/shared/theme/authored-content';
import { getLiveEditor } from '@/shared/utils/tiptapEditor';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface LessonBlockEditorProps {
  /** Initial HTML content to load into the editor */
  initialHtml: string;
  /** Called when the user triggers a save; receives the current HTML */
  onSave: (html: string) => void;
  /** Called when the user cancels editing */
  onCancel: () => void;
}

type HeadingValue = 'paragraph' | 'h1' | 'h2' | 'h3';

interface ToolbarState {
  headingValue: HeadingValue;
  canUndo: boolean;
  canRedo: boolean;
  isBoldActive: boolean;
  isItalicActive: boolean;
  isUnderlineActive: boolean;
  isAlignLeftActive: boolean;
  isAlignCenterActive: boolean;
  isAlignRightActive: boolean;
  isBulletListActive: boolean;
  isOrderedListActive: boolean;
  isLinkActive: boolean;
}

const DEFAULT_STATE: ToolbarState = {
  headingValue: 'paragraph',
  canUndo: false,
  canRedo: false,
  isBoldActive: false,
  isItalicActive: false,
  isUnderlineActive: false,
  isAlignLeftActive: false,
  isAlignCenterActive: false,
  isAlignRightActive: false,
  isBulletListActive: false,
  isOrderedListActive: false,
  isLinkActive: false,
};

/* ------------------------------------------------------------------ */
/*  Toolbar (compact)                                                  */
/* ------------------------------------------------------------------ */

const BlockToolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkValue, setLinkValue] = React.useState('');
  const [imageOpen, setImageOpen] = React.useState(false);
  const [imageValue, setImageValue] = React.useState('');
  const liveEditor = getLiveEditor(editor);

  const runEditorCommand = React.useCallback(
    (command: (instance: Editor) => void) => {
      const instance = getLiveEditor(editor);
      if (!instance) return;
      command(instance);
    },
    [editor]
  );

  const ts =
    useEditorState({
      editor: liveEditor,
      selector: ({ editor: inst }): ToolbarState => {
        if (!inst || inst.isDestroyed) return DEFAULT_STATE;

        const headingValue: HeadingValue = inst.isActive('heading', { level: 1 })
          ? 'h1'
          : inst.isActive('heading', { level: 2 })
            ? 'h2'
            : inst.isActive('heading', { level: 3 })
              ? 'h3'
              : 'paragraph';

        return {
          headingValue,
          canUndo: inst.can().chain().focus().undo().run(),
          canRedo: inst.can().chain().focus().redo().run(),
          isBoldActive: inst.isActive('bold'),
          isItalicActive: inst.isActive('italic'),
          isUnderlineActive: inst.isActive('underline'),
          isAlignLeftActive: inst.isActive({ textAlign: 'left' }),
          isAlignCenterActive: inst.isActive({ textAlign: 'center' }),
          isAlignRightActive: inst.isActive({ textAlign: 'right' }),
          isBulletListActive: inst.isActive('bulletList'),
          isOrderedListActive: inst.isActive('orderedList'),
          isLinkActive: inst.isActive('link'),
        };
      },
    }) ?? DEFAULT_STATE;

  const handleHeadingChange = (e: SelectChangeEvent<HeadingValue>) => {
    const value = e.target.value as HeadingValue;
    if (value === 'paragraph') {
      runEditorCommand((instance) => instance.chain().focus().setParagraph().run());
    } else {
      const level = value === 'h1' ? 1 : value === 'h2' ? 2 : 3;
      runEditorCommand((instance) => instance.chain().focus().setHeading({ level }).run());
    }
  };

  const openLinkDialog = () => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const prev = instance.getAttributes('link').href as string | undefined;
    setLinkValue(prev ?? 'https://');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const trimmed = linkValue.trim();
    if (!trimmed) {
      instance.chain().focus().unsetLink().run();
    } else {
      instance.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
    }
    setLinkOpen(false);
  };

  const openImageDialog = () => {
    setImageValue('https://');
    setImageOpen(true);
  };

  const applyImage = () => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const trimmed = imageValue.trim();
    if (trimmed) {
      instance.chain().focus().setImage({ src: trimmed }).run();
    }
    setImageOpen(false);
  };

  if (!liveEditor) return null;

  return (
    <>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          px: 1,
          py: 0.5,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
          {/* Undo / Redo */}
          <TBtn
            title="Undo"
            onClick={() => runEditorCommand((instance) => instance.chain().focus().undo().run())}
            disabled={!ts.canUndo}
          >
            <UndoOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn
            title="Redo"
            onClick={() => runEditorCommand((instance) => instance.chain().focus().redo().run())}
            disabled={!ts.canRedo}
          >
            <RedoOutlinedIcon fontSize="small" />
          </TBtn>

          <Divider orientation="vertical" flexItem />

          {/* Heading select */}
          <Select
            size="small"
            value={ts.headingValue}
            onChange={handleHeadingChange}
            sx={{
              height: 30,
              minWidth: 130,
              fontSize: '0.8rem',
              bgcolor: '#fff',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 },
            }}
          >
            <MenuItem value="paragraph">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextFieldsOutlinedIcon sx={{ fontSize: 16 }} /> Normal
              </Box>
            </MenuItem>
            <MenuItem value="h1">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LooksOneOutlinedIcon sx={{ fontSize: 16 }} /> Heading 1
              </Box>
            </MenuItem>
            <MenuItem value="h2">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LooksTwoOutlinedIcon sx={{ fontSize: 16 }} /> Heading 2
              </Box>
            </MenuItem>
            <MenuItem value="h3">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Looks3OutlinedIcon sx={{ fontSize: 16 }} /> Heading 3
              </Box>
            </MenuItem>
          </Select>

          <Divider orientation="vertical" flexItem />

          {/* Inline formatting */}
          <TBtn
            title="Bold"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().toggleBold().run())
            }
            active={ts.isBoldActive}
          >
            <FormatBoldOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn
            title="Italic"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().toggleItalic().run())
            }
            active={ts.isItalicActive}
          >
            <FormatItalicOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn
            title="Underline"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().toggleUnderline().run())
            }
            active={ts.isUnderlineActive}
          >
            <FormatUnderlinedOutlinedIcon fontSize="small" />
          </TBtn>

          <Divider orientation="vertical" flexItem />

          {/* Alignment */}
          <TBtn
            title="Align left"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().setTextAlign('left').run())
            }
            active={ts.isAlignLeftActive}
          >
            <FormatAlignLeftOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn
            title="Align center"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().setTextAlign('center').run())
            }
            active={ts.isAlignCenterActive}
          >
            <FormatAlignCenterOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn
            title="Align right"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().setTextAlign('right').run())
            }
            active={ts.isAlignRightActive}
          >
            <FormatAlignRightOutlinedIcon fontSize="small" />
          </TBtn>

          <Divider orientation="vertical" flexItem />

          {/* Lists & block types */}
          <TBtn
            title="Bullet list"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().toggleBulletList().run())
            }
            active={ts.isBulletListActive}
          >
            <FormatListBulletedOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn
            title="Numbered list"
            onClick={() =>
              runEditorCommand((instance) => instance.chain().focus().toggleOrderedList().run())
            }
            active={ts.isOrderedListActive}
          >
            <FormatListNumberedOutlinedIcon fontSize="small" />
          </TBtn>

          <Divider orientation="vertical" flexItem />

          {/* Link & Image */}
          <TBtn title="Link" onClick={openLinkDialog} active={ts.isLinkActive}>
            <LinkOutlinedIcon fontSize="small" />
          </TBtn>
          <TBtn title="Image" onClick={openImageDialog}>
            <ImageOutlinedIcon fontSize="small" />
          </TBtn>
        </Stack>
      </Box>

      {/* Link dialog */}
      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="URL"
            placeholder="https://example.com"
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyLink();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button onClick={applyLink} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image dialog */}
      <Dialog open={imageOpen} onClose={() => setImageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Image URL"
            placeholder="https://example.com/image.png"
            value={imageValue}
            onChange={(e) => setImageValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyImage();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageOpen(false)}>Cancel</Button>
          <Button onClick={applyImage} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Tiny toolbar button                                                */
/* ------------------------------------------------------------------ */

const TBtn: React.FC<{
  title: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, active = false, disabled = false, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          color: active ? 'primary.main' : 'text.secondary',
          bgcolor: active ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/* ------------------------------------------------------------------ */
/*  Main editor component                                              */
/* ------------------------------------------------------------------ */

const LessonBlockEditor: React.FC<LessonBlockEditorProps> = ({ initialHtml, onSave, onCancel }) => {
  // Capture the initial value at mount time only. We intentionally do NOT sync
  // later prop changes into the editor — doing so would clobber in-progress
  // edits whenever an RTK Query refetch delivers updated lesson data.
  const initialRef = useRef(initialHtml);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        code: false,
        codeBlock: false,
        link: { openOnClick: false, autolink: true },
        trailingNode: {
          notAfter: ['bulletList', 'orderedList'],
        },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: initialRef.current || '<p></p>',
    editorProps: {
      attributes: {
        spellcheck: 'true',
        class: 'lesson-block-editor',
      },
    },
    autofocus: 'end',
  });
  const liveEditor = getLiveEditor(editor);

  const handleSave = () => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    const html = instance.getHTML();
    // Treat empty editor (just an empty paragraph) as empty string
    const isEmpty = html === '<p></p>' || html === '';
    onSave(isEmpty ? '' : html);
  };

  return (
    <Box
      sx={{
        border: '2px solid',
        borderColor: 'primary.main',
        borderRadius: 2,
        overflow: 'hidden',
        // Editor chrome. Content typography comes from the shared contract below, so
        // this surface and the two read-only surfaces cannot drift apart again.
        '& .ProseMirror': {
          outline: 'none',
          minHeight: 120,
          padding: 2,
          fontSize: '1rem',
          lineHeight: 1.6,
          fontFamily: 'inherit',
          ...authoredContentSx,
        },
      }}
    >
      {liveEditor && <BlockToolbar editor={liveEditor} />}
      <EditorContent editor={liveEditor} />

      {/* Save / Cancel strip */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          p: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default LessonBlockEditor;
